import { useState } from "react";
import { AlertTriangle, Plus, X } from "lucide-react";
import { api, ApiError, type PollDto, type PollOptionDto } from "../../lib/api";
import {
  ActionMessage,
  Button,
  Card,
  Checkbox,
  Field,
  Input,
  Modal,
  MoneyInput,
  PanelSection,
  RowActions,
  Select,
  useAdminAction,
} from "./shared";

interface PollForm {
  title: string;
  description: string;
  category: string;
  opensAt: string;
  closesAt: string;
  allowChanges: boolean;
  visibility: string;
  maxChoices: number;
  rulesText: string;
  showCounts: boolean;
}

const emptyPollForm: PollForm = {
  title: "",
  description: "",
  category: "date",
  opensAt: "",
  closesAt: "",
  allowChanges: true,
  visibility: "public_results",
  maxChoices: 1,
  rulesText: "",
  showCounts: true,
};

interface OptionForm {
  label: string;
  description: string;
  published: boolean;
  costPaise: number | null;
  costKind: "per_person" | "total";
  veg: "" | "true" | "false";
  seats: string;
  departureTime: string;
  returnTime: string;
  vehicleType: string;
  vendorRef: string;
}

function optionToForm(o: PollOptionDto): OptionForm {
  const meta = o.meta as Record<string, unknown>;
  const cost = (meta.costPaise ?? meta.perPersonPaise ?? meta.contributionPaise) as number | undefined;
  return {
    label: o.label,
    description: o.description,
    published: o.published,
    costPaise: typeof cost === "number" ? cost : null,
    costKind: meta.costKind === "total" ? "total" : "per_person",
    veg: meta.veg === true ? "true" : meta.veg === false ? "false" : "",
    seats: typeof meta.seats === "number" ? String(meta.seats) : "",
    departureTime: typeof meta.departureTime === "string" ? meta.departureTime : "",
    returnTime: typeof meta.returnTime === "string" ? meta.returnTime : "",
    vehicleType: typeof meta.vehicleType === "string" ? meta.vehicleType : "",
    vendorRef: typeof meta.vendorRef === "string" ? meta.vendorRef : "",
  };
}

function formToMeta(f: OptionForm, category: string): Record<string, unknown> {
  const meta: Record<string, unknown> = {};
  if (f.costPaise !== null) {
    if (category === "contribution") meta.contributionPaise = f.costPaise;
    else if (category === "menu") meta.perPersonPaise = f.costPaise;
    else meta.costPaise = f.costPaise;
    meta.costKind = f.costKind;
  }
  if (f.veg !== "") meta.veg = f.veg === "true";
  if (f.seats !== "") meta.seats = Number(f.seats);
  if (f.departureTime) meta.departureTime = f.departureTime;
  if (f.returnTime) meta.returnTime = f.returnTime;
  if (f.vehicleType) meta.vehicleType = f.vehicleType;
  if (f.vendorRef) meta.vendorRef = f.vendorRef;
  return meta;
}

const emptyOptionForm: OptionForm = {
  label: "",
  description: "",
  published: true,
  costPaise: null,
  costKind: "per_person",
  veg: "",
  seats: "",
  departureTime: "",
  returnTime: "",
  vehicleType: "",
  vendorRef: "",
};

export default function PollsPanel({
  polls,
  onSaved,
}: {
  polls: PollDto[];
  onSaved: () => void;
}) {
  const { message, busy, run, setMessage } = useAdminAction();
  const [editingPoll, setEditingPoll] = useState<PollDto | null>(null);
  const [creating, setCreating] = useState(false);
  const [pollForm, setPollForm] = useState<PollForm>(emptyPollForm);
  const [optionTarget, setOptionTarget] = useState<{ poll: PollDto; option: PollOptionDto | null } | null>(null);
  const [optionForm, setOptionForm] = useState<OptionForm>(emptyOptionForm);

  const openCreate = () => {
    setPollForm(emptyPollForm);
    setCreating(true);
  };

  const openEdit = (poll: PollDto) => {
    setPollForm({
      title: poll.title,
      description: poll.description,
      category: poll.category,
      opensAt: poll.opensAt ? poll.opensAt.slice(0, 16) : "",
      closesAt: poll.closesAt ? poll.closesAt.slice(0, 16) : "",
      allowChanges: poll.allowChanges,
      visibility: poll.visibility,
      maxChoices: poll.maxChoices,
      rulesText: poll.rulesText,
      showCounts: poll.showCounts,
    });
    setEditingPoll(poll);
    setCreating(false);
  };

  const savePoll = () =>
    run(async () => {
      const payload = {
        title: pollForm.title,
        description: pollForm.description,
        category: pollForm.category,
        opensAt: pollForm.opensAt ? new Date(pollForm.opensAt).toISOString() : null,
        closesAt: pollForm.closesAt ? new Date(pollForm.closesAt).toISOString() : null,
        allowChanges: pollForm.allowChanges,
        visibility: pollForm.visibility,
        maxChoices: Number(pollForm.maxChoices),
        rulesText: pollForm.rulesText,
        showCounts: pollForm.showCounts,
        sortOrder: 0,
      };
      if (editingPoll) await api.adminSend(`/polls/${editingPoll.id}`, "PUT", payload);
      else await api.adminSend("/polls", "POST", payload);
      setEditingPoll(null);
      setCreating(false);
      onSaved();
    }, editingPoll ? "Poll updated." : "Poll created as a draft.");

  const pollAction = (poll: PollDto, action: string, confirmText?: string) => {
    if (confirmText && !window.confirm(confirmText)) return;
    void run(async () => {
      const res = await api.adminSend<{ warnings: string[] }>(`/polls/${poll.id}/status`, "POST", { action });
      if (res.warnings && res.warnings.length > 0) {
        setMessage({
          tone: "error",
          text: `Done, but review these warnings: ${res.warnings.join(" • ")}`,
        });
      }
      onSaved();
    }, action === "publish" ? "Poll published — it is now live for students." : `Poll ${action}d.`);
  };

  const deletePoll = (poll: PollDto) => {
    if (!window.confirm(`Delete “${poll.title}”? This cannot be undone.`)) return;
    void run(async () => {
      await api.adminSend(`/polls/${poll.id}`, "DELETE");
      onSaved();
    }, "Poll deleted.");
  };

  const openOption = (poll: PollDto, option: PollOptionDto | null) => {
    setOptionTarget({ poll, option });
    setOptionForm(option ? optionToForm(option) : emptyOptionForm);
  };

  const saveOption = () => {
    if (!optionTarget) return;
    const { poll, option } = optionTarget;
    void run(async () => {
      const payload = {
        label: optionForm.label,
        description: optionForm.description,
        metaJson: formToMeta(optionForm, poll.category),
        sortOrder: 0,
        published: optionForm.published,
      };
      if (option) await api.adminSend(`/options/${option.id}`, "PUT", payload);
      else await api.adminSend(`/polls/${poll.id}/options`, "POST", payload);
      setOptionTarget(null);
      onSaved();
    }, option ? "Option updated." : "Option added.");
  };

  const deleteOption = (option: PollOptionDto) => {
    if (!window.confirm(`Delete option “${option.label}”?`)) return;
    void run(async () => {
      await api.adminSend(`/options/${option.id}`, "DELETE");
      onSaved();
    }, "Option deleted.");
  };

  return (
    <PanelSection
      title="Polls & voting rules"
      description="Create and manage polls for the date, menu, contribution, transport, and any other decision. Draft polls and unpublished options are invisible on the public site until you publish them."
      action={<Button onClick={openCreate}><Plus className="h-4 w-4" aria-hidden="true" /> New poll</Button>}
    >
      <div className="space-y-5">
        {polls.map((poll) => (
          <Card key={poll.id} className="p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-semibold text-white">{poll.title}</h3>
                  <span className="rounded-full bg-white/[0.06] px-2.5 py-0.5 text-xs font-medium text-wandor-text">
                    {poll.status}
                  </span>
                  <span className="rounded-full bg-wandor-prompt/12 px-2.5 py-0.5 text-xs font-medium text-wandor-prompt">
                    {poll.category}
                  </span>
                  <span className="rounded-full bg-white/[0.06] px-2.5 py-0.5 text-xs text-wandor-muted">
                    visibility: {poll.visibility}
                  </span>
                </div>
                <p className="mt-1 text-sm text-wandor-muted">
                  {poll.ballotsCast} ballot(s) · {poll.options.length} option(s) ·{" "}
                  {poll.allowChanges ? "changes allowed" : "one-shot ballots"}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {poll.status !== "open" && poll.status !== "cancelled" && (
                  <Button size="sm" onClick={() => pollAction(poll, poll.status === "closed" ? "reopen" : "publish")}>
                    {poll.status === "closed" ? "Reopen" : "Publish"}
                  </Button>
                )}
                {poll.status === "open" && (
                  <Button size="sm" variant="secondary" onClick={() => pollAction(poll, "close")}>
                    Close voting
                  </Button>
                )}
                {poll.status !== "cancelled" && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => pollAction(poll, "cancel", "Cancel this poll? It will show as cancelled publicly.")}
                  >
                    Cancel
                  </Button>
                )}
                <RowActions onEdit={() => openEdit(poll)} onDelete={() => deletePoll(poll)} />
              </div>
            </div>

            <ul className="mt-4 space-y-2">
              {poll.options.map((o) => (
                <li
                  key={o.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/8 px-4 py-2.5 text-sm"
                >
                  <span className="flex items-center gap-2">
                    <span className={o.published ? "text-wandor-text" : "text-wandor-muted italic"}>
                      {o.label}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] ${o.published ? "bg-emerald-500/100/15 text-emerald-300" : "bg-amber-500/100/15 text-amber-300"}`}
                    >
                      {o.published ? "published" : "draft"}
                    </span>
                    {typeof o.count === "number" && (
                      <span className="text-xs text-wandor-muted">{o.count} vote(s)</span>
                    )}
                  </span>
                  <RowActions onEdit={() => openOption(poll, o)} onDelete={() => deleteOption(o)} />
                </li>
              ))}
            </ul>

            <Button size="sm" variant="ghost" className="mt-3" onClick={() => openOption(poll, null)}>
              <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add option
            </Button>
          </Card>
        ))}
      </div>

      {(creating || editingPoll) && (
        <Modal
          title={editingPoll ? `Edit “${editingPoll.title}”` : "Create a new poll"}
          onClose={() => {
            setCreating(false);
            setEditingPoll(null);
          }}
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Question / title" htmlFor="p-title">
              <Input
                id="p-title"
                value={pollForm.title}
                onChange={(e) => setPollForm((f) => ({ ...f, title: e.target.value }))}
                maxLength={200}
              />
            </Field>
            <Field label="Category" htmlFor="p-cat">
              <Select
                id="p-cat"
                value={pollForm.category}
                onChange={(e) => setPollForm((f) => ({ ...f, category: e.target.value }))}
              >
                <option value="date">Event date</option>
                <option value="menu">Food menu</option>
                <option value="contribution">Contribution / ticket price</option>
                <option value="transport">Transportation</option>
                <option value="venue">Venue</option>
                <option value="timing">Timing</option>
                <option value="entertainment">Entertainment</option>
                <option value="decor">Decorations</option>
                <option value="photography">Photography</option>
                <option value="other">Other</option>
              </Select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Description">
                <textarea
                  className="w-full min-h-[80px] rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm outline-none focus:border-wandor-prompt"
                  value={pollForm.description}
                  onChange={(e) => setPollForm((f) => ({ ...f, description: e.target.value }))}
                  maxLength={4000}
                />
              </Field>
            </div>
            <Field label="Opens at (optional)" htmlFor="p-open">
              <Input
                id="p-open"
                type="datetime-local"
                value={pollForm.opensAt}
                onChange={(e) => setPollForm((f) => ({ ...f, opensAt: e.target.value }))}
              />
            </Field>
            <Field label="Closes at (optional)" htmlFor="p-close">
              <Input
                id="p-close"
                type="datetime-local"
                value={pollForm.closesAt}
                onChange={(e) => setPollForm((f) => ({ ...f, closesAt: e.target.value }))}
              />
            </Field>
            <Field label="Result visibility" htmlFor="p-vis">
              <Select
                id="p-vis"
                value={pollForm.visibility}
                onChange={(e) => setPollForm((f) => ({ ...f, visibility: e.target.value }))}
              >
                <option value="public_results">Public results (always visible)</option>
                <option value="hidden_until_closed">Hidden until the poll closes</option>
                <option value="private">Private (never shown publicly)</option>
              </Select>
            </Field>
            <Field label="Max choices per ballot" htmlFor="p-max">
              <Input
                id="p-max"
                type="number"
                min={1}
                max={20}
                value={pollForm.maxChoices}
                onChange={(e) => setPollForm((f) => ({ ...f, maxChoices: Number(e.target.value) }))}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Rules text (shown on the poll card)">
                <textarea
                  className="w-full min-h-[60px] rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm outline-none focus:border-wandor-prompt"
                  value={pollForm.rulesText}
                  onChange={(e) => setPollForm((f) => ({ ...f, rulesText: e.target.value }))}
                  maxLength={2000}
                />
              </Field>
            </div>
            <Checkbox
              label="Allow students to change their ballot before the deadline"
              checked={pollForm.allowChanges}
              onChange={(e) => setPollForm((f) => ({ ...f, allowChanges: e.target.checked }))}
            />
            <Checkbox
              label="Show vote counts and percentages publicly"
              checked={pollForm.showCounts}
              onChange={(e) => setPollForm((f) => ({ ...f, showCounts: e.target.checked }))}
            />
          </div>

          <div className="mt-6 flex items-center justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setCreating(false);
                setEditingPoll(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={savePoll} loading={busy}>
              {editingPoll ? "Save poll" : "Create draft poll"}
            </Button>
          </div>
          <ActionMessage message={message} />
        </Modal>
      )}

      {optionTarget && (
        <Modal
          title={
            optionTarget.option
              ? `Edit option “${optionTarget.option.label}”`
              : `Add option to “${optionTarget.poll.title}”`
          }
          onClose={() => setOptionTarget(null)}
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Option label" htmlFor="o-label">
              <Input
                id="o-label"
                value={optionForm.label}
                onChange={(e) => setOptionForm((f) => ({ ...f, label: e.target.value }))}
                maxLength={200}
              />
            </Field>
            <Field label="Price / cost (₹)" hint="Leave empty for non-priced options.">
              <MoneyInput
                label="hidden"
                paise={optionForm.costPaise}
                onChangePaise={(p) => setOptionForm((f) => ({ ...f, costPaise: p }))}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Description">
                <textarea
                  className="w-full min-h-[70px] rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm outline-none focus:border-wandor-prompt"
                  value={optionForm.description}
                  onChange={(e) => setOptionForm((f) => ({ ...f, description: e.target.value }))}
                  maxLength={2000}
                />
              </Field>
            </div>
            <Field label="Cost type" htmlFor="o-costkind">
              <Select
                id="o-costkind"
                value={optionForm.costKind}
                onChange={(e) =>
                  setOptionForm((f) => ({ ...f, costKind: e.target.value as OptionForm["costKind"] }))
                }
              >
                <option value="per_person">Per person</option>
                <option value="total">Total</option>
              </Select>
            </Field>
            <Field label="Veg / Non-veg" htmlFor="o-veg">
              <Select
                id="o-veg"
                value={optionForm.veg}
                onChange={(e) => setOptionForm((f) => ({ ...f, veg: e.target.value as OptionForm["veg"] }))}
              >
                <option value="">Not applicable</option>
                <option value="true">Vegetarian</option>
                <option value="false">Non-vegetarian</option>
              </Select>
            </Field>
            <Field label="Seats (transport)" htmlFor="o-seats">
              <Input
                id="o-seats"
                type="number"
                min={0}
                value={optionForm.seats}
                onChange={(e) => setOptionForm((f) => ({ ...f, seats: e.target.value }))}
              />
            </Field>
            <Field label="Departure / pickup time" htmlFor="o-dep">
              <Input
                id="o-dep"
                value={optionForm.departureTime}
                onChange={(e) => setOptionForm((f) => ({ ...f, departureTime: e.target.value }))}
                placeholder="e.g. 4:30 PM"
                maxLength={40}
              />
            </Field>
            <Field label="Return time" htmlFor="o-ret">
              <Input
                id="o-ret"
                value={optionForm.returnTime}
                onChange={(e) => setOptionForm((f) => ({ ...f, returnTime: e.target.value }))}
                placeholder="e.g. 9:30 PM"
                maxLength={40}
              />
            </Field>
            <Field label="Vehicle type" htmlFor="o-veh">
              <Input
                id="o-veh"
                value={optionForm.vehicleType}
                onChange={(e) => setOptionForm((f) => ({ ...f, vehicleType: e.target.value }))}
                placeholder="e.g. 32-seater bus"
                maxLength={80}
              />
            </Field>
            <Field label="Vendor / quote reference" htmlFor="o-vendor">
              <Input
                id="o-vendor"
                value={optionForm.vendorRef}
                onChange={(e) => setOptionForm((f) => ({ ...f, vendorRef: e.target.value }))}
                maxLength={200}
              />
            </Field>
            <div className="sm:col-span-2">
              <Checkbox
                label="Published (visible to students when the poll is open)"
                checked={optionForm.published}
                onChange={(e) => setOptionForm((f) => ({ ...f, published: e.target.checked }))}
              />
            </div>
          </div>

          {!optionForm.published && (
            <p className="mt-3 flex items-start gap-2 rounded-xl bg-amber-500/10 px-4 py-2.5 text-xs text-amber-300">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              This option will stay hidden (draft) until you mark it published. The public site will warn
              students that options are not final.
            </p>
          )}

          <div className="mt-6 flex items-center justify-end gap-3">
            <Button variant="secondary" onClick={() => setOptionTarget(null)}>
              <X className="h-4 w-4" aria-hidden="true" /> Cancel
            </Button>
            <Button onClick={saveOption} loading={busy}>
              {optionTarget.option ? "Save option" : "Add option"}
            </Button>
          </div>
          <ActionMessage message={message} />
        </Modal>
      )}

      <ActionMessage message={message} />
    </PanelSection>
  );
}

// Re-exported for panels that surface API errors inline.
export { ApiError };
